/**
 * DOCX -> HTML conversion for the Notice Editor.
 *
 * The No-Code Automation source ran this in a Web Worker created with
 * `new Worker(new URL("../workers/mammothWorker.js", import.meta.url), { type: "module" })`.
 * That form is Vite-specific: react-scripts 3.4.1 / webpack 4 cannot resolve
 * `import.meta.url` worker URLs, and the source already carried a main-thread
 * fallback for when the worker was unavailable.
 *
 * The conversion logic below is the worker body unchanged - mammoth for the document
 * body plus the lightweight word/styles.xml + word/document.xml scrape that recovers
 * paragraph alignment, spacing and indentation. It now runs inline and returns the HTML
 * instead of posting it back, so the richer style extraction applies on every upload
 * rather than only when the worker happened to win the race.
 */
import * as mammoth from 'mammoth';
import JSZip from 'jszip';

/**
 * @param {ArrayBuffer} arrayBuffer raw .docx bytes
 * @returns {Promise<string>} HTML with Word paragraph styling re-applied
 */
export async function convertDocxToHtml(arrayBuffer) {
    try {
        let convertFn = mammoth.convertToHtml;
        if (!convertFn && mammoth.default?.convertToHtml) {
            convertFn = mammoth.default.convertToHtml;
        }
        if (!convertFn) {
            throw new Error('mammoth.convertToHtml is missing.');
        }

        // Convert document body via mammoth
        const result = await convertFn({ arrayBuffer }, { ignoreEmptyParagraphs: false });
        let html = result.value || '';
        
        // Filter out margin certificate number annotations outside content area
        html = html.replace(/<p[^>]*>\s*(?:e-?stamp\s+)?certificate\s+(?:no|number)[\s.:]*[A-Z0-9\-_/\s]+<\/p>/gi, '');

        // Fast, lightweight XML style extraction (spacing, alignment, indents)
        try {
            const zip = await JSZip.loadAsync(arrayBuffer);
            let defaultBefore = '';
            let defaultAfter = '';
            const stylesXmlFile = zip.file('word/styles.xml');
            if (stylesXmlFile) {
                const stylesXmlText = await stylesXmlFile.async('text');
                const docDefMatch = stylesXmlText.match(/<w:docDefaults>[\s\S]*?<\/w:docDefaults>/i);
                const normalMatch = stylesXmlText.match(/<w:style[^>]*w:styleId="Normal"[^>]*>[\s\S]*?<\/w:style>/i);
                const defSnippet = (docDefMatch ? docDefMatch[0] : '') + ' ' + (normalMatch ? normalMatch[0] : '');
                const spMatch = defSnippet.match(/<w:spacing\b([^>]*)\/?>/i);
                if (spMatch) {
                    const spAttrs = spMatch[1];
                    const beforeMatch = spAttrs.match(/w:before="(\d+)"/i);
                    const afterMatch = spAttrs.match(/w:after="(\d+)"/i);
                    if (beforeMatch) {
                        const numBefore = parseInt(beforeMatch[1], 10);
                        if (!isNaN(numBefore) && numBefore > 0) {
                            defaultBefore = `margin-top: ${(numBefore / 20).toFixed(1)}pt`;
                        }
                    }
                    if (afterMatch) {
                        const numAfter = parseInt(afterMatch[1], 10);
                        if (!isNaN(numAfter) && numAfter > 0) {
                            defaultAfter = `margin-bottom: ${(numAfter / 20).toFixed(1)}pt`;
                        }
                    }
                }
            }

            const docXmlFile = zip.file('word/document.xml');
            if (docXmlFile) {
                const xmlText = await docXmlFile.async('text');
                const pStyles = [];
                let pStart = 0;
                while ((pStart = xmlText.indexOf('<w:p', pStart)) !== -1) {
                    const nextChar = xmlText.charCodeAt(pStart + 4);
                    if (nextChar !== 32 && nextChar !== 62 && nextChar !== 47) {
                        pStart += 4;
                        continue;
                    }
                    const pEnd = xmlText.indexOf('</w:p>', pStart);
                    if (pEnd === -1) break;
                    
                    const pContent = xmlText.substring(pStart, pEnd + 6);
                    pStart = pEnd + 6;

                    const pPrStart = pContent.indexOf('<w:pPr');
                    if (pPrStart === -1) {
                        const fallbackStyles = [];
                        if (defaultBefore) fallbackStyles.push(defaultBefore);
                        if (defaultAfter) fallbackStyles.push(defaultAfter);
                        pStyles.push(fallbackStyles.join('; '));
                        continue;
                    }
                    const pPrEnd = pContent.indexOf('</w:pPr>', pPrStart);
                    const pPr = pPrEnd !== -1 ? pContent.substring(pPrStart, pPrEnd + 8) : pContent.substring(pPrStart, pPrStart + 1000);

                    const styles = [];

                    // 1. Justification / Alignment
                    const jcIdx = pPr.indexOf('<w:jc');
                    if (jcIdx !== -1) {
                        const valMatch = pPr.substring(jcIdx, jcIdx + 60).match(/w:val="([^"]+)"/i);
                        if (valMatch) {
                            const jcVal = valMatch[1].toLowerCase();
                            if (jcVal === 'right' || jcVal === 'end') styles.push('text-align: right');
                            else if (jcVal === 'center') styles.push('text-align: center');
                            else if (jcVal === 'both' || jcVal === 'distribute') styles.push('text-align: justify');
                            else if (jcVal === 'left' || jcVal === 'start') styles.push('text-align: left');
                        }
                    }

                    // 2. Spacing
                    const spIdx = pPr.indexOf('<w:spacing');
                    if (spIdx !== -1) {
                        const spSnippet = pPr.substring(spIdx, spIdx + 200);
                        const beforeMatch = spSnippet.match(/w:before="(\d+)"/i);
                        const afterMatch = spSnippet.match(/w:after="(\d+)"/i);

                        if (beforeMatch) {
                            const numBefore = parseInt(beforeMatch[1], 10);
                            if (!isNaN(numBefore) && numBefore > 0) {
                                styles.push(`margin-top: ${(numBefore / 20).toFixed(1)}pt`);
                            }
                        } else if (defaultBefore) {
                            styles.push(defaultBefore);
                        }

                        if (afterMatch) {
                            const numAfter = parseInt(afterMatch[1], 10);
                            if (!isNaN(numAfter) && numAfter > 0) {
                                styles.push(`margin-bottom: ${(numAfter / 20).toFixed(1)}pt`);
                            }
                        } else if (defaultAfter) {
                            styles.push(defaultAfter);
                        }
                    } else {
                        if (defaultBefore) styles.push(defaultBefore);
                        if (defaultAfter) styles.push(defaultAfter);
                    }

                    // 3. Indentation
                    const indIdx = pPr.indexOf('<w:ind');
                    if (indIdx !== -1) {
                        const indSnippet = pPr.substring(indIdx, indIdx + 200);
                        const leftMatch = indSnippet.match(/w:left="(\d+)"/i);
                        const firstLineMatch = indSnippet.match(/w:firstLine="(\d+)"/i);
                        const hangingMatch = indSnippet.match(/w:hanging="(\d+)"/i);

                        if (firstLineMatch) {
                            const numFirst = parseInt(firstLineMatch[1], 10);
                            if (!isNaN(numFirst) && numFirst > 0) styles.push(`text-indent: ${(numFirst / 20).toFixed(1)}pt`);
                        }
                        if (hangingMatch) {
                            const numHanging = parseInt(hangingMatch[1], 10);
                            if (!isNaN(numHanging) && numHanging > 0) {
                                styles.push(`text-indent: -${(numHanging / 20).toFixed(1)}pt`);
                                styles.push(`padding-left: ${(numHanging / 20).toFixed(1)}pt`);
                            }
                        }
                        if (leftMatch) {
                            const numLeft = parseInt(leftMatch[1], 10);
                            if (!isNaN(numLeft) && numLeft > 0) styles.push(`margin-left: ${(numLeft / 20).toFixed(1)}pt`);
                        }
                    }

                    pStyles.push(styles.join('; '));
                }

                // Inject extracted styles into HTML <p> tags
                let pIdx = 0;
                html = html.replace(/<p\b([^>]*)>/gi, (match, existingAttrs) => {
                    const styleToAdd = pIdx < pStyles.length ? pStyles[pIdx] : '';
                    pIdx++;
                    if (!styleToAdd) return match;

                    const styleMatch = existingAttrs.match(/style="([^"]*)"/i);
                    if (styleMatch) {
                        const newStyle = `${styleMatch[1]}; ${styleToAdd}`;
                        return `<p${existingAttrs.replace(/style="[^"]*"/i, `style="${newStyle}"`)}>`;
                    } else {
                        return `<p style="${styleToAdd}"${existingAttrs}>`;
                    }
                });
            }
        } catch (zipErr) {
            console.warn('Could not extract docx spacing:', zipErr);
        }
        return html;
    }
    catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
    }
}
